import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Button, Spinner, Table } from "reactstrap";
import moment from "moment";
import queryString from "query-string";

import api from "../../../services/api";
import { useCompanies } from "../../../hooks/companies";
import { useDateFilter } from "../../../hooks/datefilter";
import PageHero, {
  PageHeroExportButton,
  PageHeroRefreshButton,
} from "../../../components/PageHero";
import FilterBand from "../../../components/FilterBand";
import MetricCard from "../../../components/MetricCard";
import MetricCardGroup from "../../../components/MetricCardGroup";
import ShellCard, { TableSurface } from "../../../components/ShellCard";
import SortHeader from "../../../components/SortHeader";
import TablePagination from "../../../components/TablePagination";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";
import { formatDate } from "../../../utils/formatDate";
import { exportCsv } from "../../../utils/exportCsv";
import { sortRows, toggleSort } from "../../../utils/tableHelpers";
import ModalOcorrencia from "../Tracking/components/ModalOcorrencia";

import "./styles.css";

// Tela no padrão do Hub (PO1-2725): PageHero + MetricCardGroup + FilterBand
// (server) + ShellCard/TableSurface + SortHeader + TablePagination.
//
// Os tiles vêm de /dashboard/performance/entregas-kpi e a lista de
// /trackfy/entregas-pendentes. As duas rotas aplicam a MESMA regra canônica de
// status (PO1-2820), então o número do tile e o total da lista sempre batem.

// Os estados não terminais. A explicação de cada um sai da regra canônica —
// não é texto livre: se a regra mudar no backend, estes textos mudam junto.
// `somenteSeHouver`: o card só aparece com contagem > 0 (estados raros, que
// dependem do módulo de despacho), mas entra no total e no filtro sempre.
export const STATUS_EM_ABERTO = [
  {
    valor: "atrasada",
    rotulo: "Atrasada",
    tom: "pink",
    icone: "tim-icons icon-alert-circle-exc",
    ajuda: "Previsão vencida e sem entrega registrada. Inclui as sem retorno.",
  },
  {
    // Sub-estado de "atrasada" (mesmo status na API, flag `semRetorno`): é o
    // pior cenário da gestão de entregas — venceu e a transportadora nunca
    // disse nada, então não há nem o que tratar. Com ocorrência, ao menos se
    // sabe o que está acontecendo.
    valor: "atrasada",
    semRetorno: true,
    rotulo: "Atrasada sem retorno",
    tom: "pink",
    icone: "tim-icons icon-bulb-63",
    ajuda: "Vencida e a transportadora não registrou nenhuma ocorrência: ninguém sabe onde está.",
  },
  {
    valor: "em_transito",
    rotulo: "A caminho",
    tom: "info",
    icone: "tim-icons icon-delivery-fast",
    ajuda: "Tem ocorrência e a previsão ainda não venceu.",
  },
  {
    valor: "pendente",
    rotulo: "Em preparação",
    tom: "neutral",
    icone: "tim-icons icon-bag-16",
    ajuda: "Sem ocorrência e previsão no futuro.",
  },
  {
    valor: "sem_previsao",
    rotulo: "Sem previsão",
    tom: "warning",
    icone: "tim-icons icon-calendar-60",
    ajuda: "Sem previsão calculada; não dá para dizer se está no prazo.",
  },
  {
    // PO1-3401: a NF-e informa como transportador uma empresa do próprio
    // embarcador. Não existe transportadora para mandar rastreio; a entrega
    // fecha com a baixa feita pelo próprio embarcador.
    valor: "entrega_propria",
    rotulo: "Entrega própria",
    tom: "info",
    icone: "tim-icons icon-bus-front-12",
    ajuda: "Entregue pela própria empresa, sem transportadora contratada. Fecha com a baixa da entrega.",
  },
  {
    // PO1-2374 (H1-B): estados pré-entrega derivados do lote de despacho e do
    // envio (NotaFiscalEnvio). A API já os devolve na lista e no KPI; sem eles
    // aqui o total "no período" não batia com a soma dos cards.
    valor: "despachado",
    rotulo: "Despachado",
    tom: "info",
    icone: "tim-icons icon-send",
    ajuda: "Em lote de despacho já despachado; a transportadora ainda não confirmou o recebimento.",
    somenteSeHouver: true,
  },
  {
    valor: "etiqueta_emitida",
    rotulo: "Etiqueta emitida",
    tom: "neutral",
    icone: "tim-icons icon-tag",
    ajuda: "Etiqueta gerada na transportadora, aguardando o despacho.",
    somenteSeHouver: true,
  },
];

// Pílula da tabela reaproveita o tom do tile: uma cor por status, um lugar só.
const TOM_DO_STATUS = STATUS_EM_ABERTO.reduce((acc, item) => {
  acc[item.valor] = item.tom;
  return acc;
}, {});

const TABLE_COLUMNS = [
  { key: "numero", label: "Nota fiscal", sortField: "numero" },
  { key: "numero_pedido", label: "Pedido", sortField: "numero_pedido" },
  { key: "destinatario", label: "Destinatário", sortField: "destinatario" },
  { key: "destino", label: "Destino", sortField: "destino" },
  { key: "transportadora", label: "Transportadora", sortField: "transportadora" },
  { key: "previsao", label: "Previsão", sortField: "previsao_entrega" },
  { key: "dias", label: "Dias em aberto", sortField: "dias_em_aberto", numeric: true },
  { key: "status", label: "Status", sortField: "statusExibido" },
  { key: "ocorrencia", label: "Última ocorrência", sortField: "ultimaOcorrenciaNome" },
];

const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;
const DIAS_PADRAO = 90;

const statusDaUrl = (valor) => {
  if (!valor) return [];
  const lista = Array.isArray(valor) ? valor : [valor];
  return lista.filter((item) => STATUS_EM_ABERTO.some((s) => s.valor === item));
};

const fmtNumero = (v) =>
  v === null || v === undefined ? "-" : Number(v).toLocaleString("pt-BR");

const EntregasPendentes = ({ location }) => {
  const { empresaId, transportadoraId, arrayParams } = useCompanies();
  const { startDate, endDate, setStartDate, setEndDate } = useDateFilter();

  const filtrosIniciais = useMemo(
    () => queryString.parse((location && location.search) || ""),
    [location]
  );

  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpi, setKpi] = useState({ porStatus: {}, emAberto: 0, atrasadasSemRetorno: 0 });
  const [pagina, setPagina] = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [statusSelecionados, setStatusSelecionados] = useState(() =>
    statusDaUrl(filtrosIniciais.status)
  );
  const [buscaRascunho, setBuscaRascunho] = useState("");
  const [busca, setBusca] = useState("");
  // Tile "Atrasada sem retorno": status atrasada + só NF sem nenhuma ocorrência.
  // Chega pela URL (`semRetorno=1`) no drill-down do card do dashboard.
  const [semRetorno, setSemRetorno] = useState(
    () => filtrosIniciais.semRetorno === "1" || filtrosIniciais.semRetorno === "true"
  );
  const [sort, setSort] = useState({ field: null, dir: null });
  const [modalAberto, setModalAberto] = useState(false);
  const [nfSelecionada, setNfSelecionada] = useState(null);

  // O período da URL (drill-down vindo do KPI do dashboard) SEMEIA o filtro
  // global em vez de competir com ele — senão o botão "Período" da navbar
  // parava de responder para quem chegasse por um card.
  useEffect(() => {
    if (filtrosIniciais.dataInicial && filtrosIniciais.dataFinal) {
      setStartDate(moment(filtrosIniciais.dataInicial));
      setEndDate(moment(filtrosIniciais.dataFinal));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A tela vive em Operação, fora do dashboard, então o filtro global costuma
  // chegar vazio. Sem janela padrão a consulta varreria a NotaFiscal inteira.
  const periodo = useMemo(() => {
    const inicio = startDate
      ? moment(startDate)
      : moment().subtract(DIAS_PADRAO, "days");
    const fim = endDate ? moment(endDate) : moment();

    return {
      dataInicial: inicio.format("YYYY-MM-DD 00:00:00"),
      dataFinal: fim.format("YYYY-MM-DD 23:59:59"),
    };
  }, [startDate, endDate]);

  // Escopo de embarcador NÃO vai na query: a API resolve por
  // req.user.embarcadorId. Transportadora, sim, é filtro de verdade.
  const paramsRecorte = useMemo(() => {
    const params = { ...periodo };
    if (arrayParams.transportadoras) {
      params.transportadoras = arrayParams.transportadoras;
    }
    return params;
  }, [periodo, arrayParams.transportadoras]);

  // Os tiles são o universo do recorte (período + transportadora) e por isso
  // NÃO acompanham a busca textual: eles são o controle de filtro de status,
  // e o número que a lista devolve aparece no cabeçalho da tabela.
  const carregarKpi = useCallback(async () => {
    try {
      const { status, data } = await api.get("/dashboard/performance/entregas-kpi", {
        params: paramsRecorte,
      });

      if (status !== 200) return;

      setKpi({
        emAberto: data.emAberto || 0,
        atrasadasSemRetorno: data.atrasadasSemRetorno || 0,
        porStatus: (data.porStatus || []).reduce((acc, item) => {
          acc[item.status] = item.quantidade;
          return acc;
        }, {}),
      });
    } catch (_) {
      setKpi({ porStatus: {}, emAberto: 0, atrasadasSemRetorno: 0 });
    }
  }, [paramsRecorte]);

  const carregarLista = useCallback(async () => {
    setLoading(true);
    setErro("");

    try {
      const params = { ...paramsRecorte, pagina, tamanhoPagina };
      if (statusSelecionados.length) params.status = statusSelecionados;
      if (busca) params.busca = busca;
      if (semRetorno) params.semRetorno = true;

      const { status, data } = await api.get("/trackfy/entregas-pendentes", { params });

      if (status === 200) {
        setItens(data.itens || []);
        setTotal(data.total || 0);
      }
    } catch (_) {
      setItens([]);
      setTotal(0);
      setErro("Não conseguimos carregar as entregas em aberto agora.");
    } finally {
      setLoading(false);
    }
  }, [paramsRecorte, pagina, tamanhoPagina, statusSelecionados, busca, semRetorno]);

  useEffect(() => {
    carregarKpi();
  }, [carregarKpi]);

  useEffect(() => {
    carregarLista();
  }, [carregarLista]);

  // Qualquer troca de recorte volta para a página 1: senão a paginação aponta
  // para um offset que não existe mais.
  useEffect(() => {
    setPagina(1);
  }, [empresaId, transportadoraId, periodo, statusSelecionados, busca, semRetorno]);

  const alternarStatus = useCallback((valor) => {
    // Qualquer tile de status desliga o "sem retorno": ele só faz sentido
    // sozinho, sobre atrasada.
    setSemRetorno(false);
    setStatusSelecionados((atual) =>
      atual.includes(valor)
        ? atual.filter((item) => item !== valor)
        : [...atual, valor]
    );
  }, []);

  // PO1-3888: o "sem retorno" troca o recorte de status por ["atrasada"]; o
  // segundo clique devolve o recorte que havia antes do primeiro.
  const statusAntesSemRetornoRef = useRef([]);
  const alternarSemRetorno = useCallback(() => {
    if (semRetorno) {
      setSemRetorno(false);
      setStatusSelecionados(statusAntesSemRetornoRef.current);
      return;
    }
    statusAntesSemRetornoRef.current = statusSelecionados;
    setSemRetorno(true);
    setStatusSelecionados(["atrasada"]);
  }, [semRetorno, statusSelecionados]);

  const handleSort = useCallback((field) => {
    setSort((atual) => toggleSort(atual, field));
  }, []);

  const limpar = useCallback(() => {
    setBuscaRascunho("");
    setBusca("");
    setStatusSelecionados([]);
    setSemRetorno(false);
  }, []);

  const abrirHistorico = useCallback((idNf) => {
    setNfSelecionada(idNf);
    setModalAberto(true);
  }, []);

  const linhas = useMemo(
    () =>
      itens.map((nf) => ({
        ...nf,
        previsaoFormatada: nf.previsao_entrega
          ? formatDate(nf.previsao_entrega)
          : "Sem previsão",
        destino: nf.cidade_entrega
          ? `${nf.cidade_entrega}/${nf.uf_entrega || ""}`.replace(/\/$/, "")
          : "-",
        ultimaOcorrenciaNome:
          (nf.ultimaOcorrencia && nf.ultimaOcorrencia.nome) ||
          "Sem ocorrência registrada",
        // O rótulo da API é "Atrasada" para as duas; a tela destaca o pior caso.
        statusExibido:
          nf.status === "atrasada" && nf.semRetorno
            ? "Atrasada · sem retorno"
            : nf.statusLabel,
      })),
    [itens]
  );

  // Ordena a página aberta, igual à Consulta de Notas Fiscais: a ordenação
  // canônica (previsão mais antiga primeiro) vem do backend.
  const linhasOrdenadas = useMemo(() => sortRows(linhas, sort), [linhas, sort]);

  const exportar = useCallback(() => {
    exportCsv({
      filename: "entregas-pendentes",
      columns: [
        { key: "numero", label: "Nota fiscal" },
        { key: "numero_pedido", label: "Pedido" },
        { key: "destinatario", label: "Destinatário" },
        { key: "cep_entrega", label: "CEP" },
        { key: "destino", label: "Destino" },
        { key: "transportadora", label: "Transportadora" },
        { key: "previsaoFormatada", label: "Previsão" },
        { key: "dias_em_aberto", label: "Dias em aberto" },
        { key: "statusExibido", label: "Status" },
        { key: "ultimaOcorrenciaNome", label: "Última ocorrência" },
      ],
      rows: linhasOrdenadas,
    });
  }, [linhasOrdenadas]);

  const filtrosAtivos =
    statusSelecionados.length + (busca ? 1 : 0) + (semRetorno ? 1 : 0);
  const periodoLegivel = `${moment(periodo.dataInicial).format(
    "DD/MM/YYYY"
  )} a ${moment(periodo.dataFinal).format("DD/MM/YYYY")}`;

  return (
    <div className="content entregas-pendentes">
      <ModalOcorrencia
        modal={modalAberto}
        toggle={() => setModalAberto(false)}
        notaFiscal={nfSelecionada}
      />

      <PageHero
        eyebrow="Trackfy"
        title="Entregas Pendentes"
        description={`Notas fiscais sem entrega registrada, emitidas entre ${periodoLegivel}. Ajuste a janela no botão Período, no topo da página.`}
        actions={
          <>
            <PageHeroRefreshButton
              onClick={() => {
                carregarKpi();
                carregarLista();
              }}
              loading={loading}
            />
            <PageHeroExportButton
              onClick={exportar}
              disabled={!linhasOrdenadas.length}
            />
          </>
        }
      />

      <MetricCardGroup
        title="Entregas em aberto"
        description="Clique num indicador para filtrar a lista abaixo."
        action={
          <span className="entregas-pendentes__contagem">
            {fmtNumero(kpi.emAberto)} no período
          </span>
        }
      >
        {STATUS_EM_ABERTO.filter(
          (item) => !item.somenteSeHouver || Number(kpi.porStatus[item.valor] || 0) > 0 || statusSelecionados.includes(item.valor)
        ).map((item) =>
          item.semRetorno ? (
            <MetricCard
              key={`${item.valor}_sem_retorno`}
              label={item.rotulo}
              value={fmtNumero(kpi.atrasadasSemRetorno)}
              helper={item.ajuda}
              iconClassName={item.icone}
              tone={item.tom}
              onClick={alternarSemRetorno}
              active={semRetorno}
            />
          ) : (
            <MetricCard
              key={item.valor}
              label={item.rotulo}
              value={fmtNumero(kpi.porStatus[item.valor] || 0)}
              helper={item.ajuda}
              iconClassName={item.icone}
              tone={item.tom}
              onClick={() => alternarStatus(item.valor)}
              active={!semRetorno && statusSelecionados.includes(item.valor)}
            />
          )
        )}
      </MetricCardGroup>

      <FilterBand
        mode="server"
        searchLabel="Buscar entrega"
        searchPlaceholder="Nota fiscal, pedido ou destinatário"
        searchHint="A busca vale sobre todo o período, não só sobre a página aberta."
        searchValue={buscaRascunho}
        onSearchChange={setBuscaRascunho}
        onSearchSubmit={setBusca}
        activeFiltersCount={filtrosAtivos}
        extra={
          filtrosAtivos ? (
            <Button size="sm" color="default" outline onClick={limpar} disabled={loading}>
              Limpar filtros
            </Button>
          ) : null
        }
      />

      <ShellCard
        title="Notas em aberto"
        description={`${fmtNumero(total)} nota(s) no recorte atual`}
      >
        <TableSurface>
          {erro ? (
            <div className="entregas-pendentes__estado entregas-pendentes__estado--erro">
              {erro}
            </div>
          ) : null}

          {!erro && loading ? (
            <div className="entregas-pendentes__estado">
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} /> Consultando
              entregas...
            </div>
          ) : null}

          {!erro && !loading && linhasOrdenadas.length === 0 ? (
            <div className="entregas-pendentes__estado">
              Nenhuma entrega em aberto neste recorte. Amplie a janela no botão
              Período ou limpe os filtros.
            </div>
          ) : null}

          {!erro && !loading && linhasOrdenadas.length > 0 ? (
            <Table
              className="entregas-pendentes__tabela hub-thead-accent"
              size="sm"
              responsive
              hover
            >
              <thead className="text-primary">
                <tr>
                  {TABLE_COLUMNS.map((col) => (
                    <th key={col.key} className={col.numeric ? "is-number" : undefined}>
                      <SortHeader
                        label={col.label}
                        field={col.sortField}
                        sort={sort}
                        onSort={handleSort}
                        align={col.numeric ? "right" : "left"}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {linhasOrdenadas.map((nf) => (
                  <tr key={nf.id} onClick={() => abrirHistorico(nf.id)}>
                    <td>
                      <strong>{nf.numero}</strong>
                      <small>{nf.cep_entrega ? `CEP ${nf.cep_entrega}` : "Sem CEP"}</small>
                    </td>
                    <td>{nf.numero_pedido || "-"}</td>
                    <td>
                      <strong>{nf.destinatario || "-"}</strong>
                    </td>
                    <td title={nf.destino}>{nf.destino}</td>
                    <td title={nf.transportadora || ""}>{nf.transportadora || "-"}</td>
                    <td className="entregas-pendentes__nowrap">{nf.previsaoFormatada}</td>
                    {/* Sem previsão não é zero dia em aberto, é não calculável. */}
                    <td className="is-number">
                      {nf.dias_em_aberto === null || nf.dias_em_aberto === undefined
                        ? "-"
                        : fmtNumero(nf.dias_em_aberto)}
                    </td>
                    <td>
                      <span
                        className={`entregas-pendentes__status entregas-pendentes__status--${
                          TOM_DO_STATUS[nf.status] || "neutral"
                        }`}
                      >
                        {nf.statusExibido}
                      </span>
                    </td>
                    <td title={nf.ultimaOcorrenciaNome}>
                      {nf.ultimaOcorrencia ? (
                        nf.ultimaOcorrenciaNome
                      ) : (
                        <span className="entregas-pendentes__muted">{nf.ultimaOcorrenciaNome}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : null}

          <TablePagination
            page={pagina}
            pageSize={tamanhoPagina}
            total={total}
            pageSizes={PAGE_SIZES}
            itemLabel="nota(s)"
            disabled={loading}
            onPageChange={setPagina}
            onPageSizeChange={(tamanho) => {
              setTamanhoPagina(tamanho);
              setPagina(1);
            }}
          />
        </TableSurface>
      </ShellCard>
    </div>
  );
};

export default EntregasPendentes;
